import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, shareReplay } from 'rxjs/operators';
import {
  AuthAccessTokenDetails,
  DecodedToken,
  LoginOAuthParams,
  OauthAEMConfig,
  RedirectfulResponse
} from '../../../../models/login';
import { AppTokenHelperService } from './app-token-helper.service';

@Injectable({
  providedIn: 'root'
})
export class AppTokenService extends AppTokenHelperService {
  private tokenRequests = new Map<string, Observable<AuthAccessTokenDetails>>();

  // eslint-disable-next-line complexity
  getAppToken(config: OauthAEMConfig): Observable<AuthAccessTokenDetails> {
    const token = this.getExistingToken(config);

    if (token && token.access_token && this.isTokenValid(config, token)) {
      return of(token);
    }

    const clientId = config.CLIENT_ID_VALUE;
    const existingRequest = this.tokenRequests.get(clientId);

    if (existingRequest) {
      return existingRequest;
    }

    const request$ = from(this.isHeaderTokenAvailable()).pipe(
      concatMap((response: AuthAccessTokenDetails) => {
        if (
          response?.access_token &&
          config.CLIENT_ID_VALUE === config.HEADER_CLIENT_ID_VALUE &&
          this.isTokenValid(config, response)
        ) {
          this.tokenMap.set(config.CLIENT_ID_VALUE, response);
          return of(response as AuthAccessTokenDetails);
        } else {
          const pkceCode = this.loginRememberMeUtils.generateCodeChallenge();
          const loginOAuthParams: LoginOAuthParams = {
            oauthConfig: config,
            pkceCode: pkceCode
          };
          return this.loginRememberMeWrapper
            .initiatePingFlow(
              loginOAuthParams,
              'INITIATE_LOGIN',
              undefined,
              true
            )
            .pipe(
              concatMap(
                (
                  response:
                    | AuthAccessTokenDetails
                    | RedirectfulResponse
                    | boolean
                    | string
                ) => {
                  if (
                    typeof response === 'string' ||
                    typeof response === 'boolean'
                  ) {
                    return of({} as AuthAccessTokenDetails);
                  } else if ('access_token' in response) {
                    this.tokenMap.set(config.CLIENT_ID_VALUE, response);
                    this.sendHeaderTokenServiceWorker(config, response);
                    return of(response);
                  } else {
                    return of({} as AuthAccessTokenDetails);
                  }
                }
              ),
              catchError((error) => {
                this.logging.log(error);
                return of({} as AuthAccessTokenDetails);
              })
            );
        }
      }),
      shareReplay(1)
    );

    this.tokenRequests.set(clientId, request$);
    request$.subscribe({
      complete: () => this.tokenRequests.delete(clientId),
      error: () => this.tokenRequests.delete(clientId)
    });

    return request$;
  }

  getExistingToken(config: OauthAEMConfig): AuthAccessTokenDetails {
    return (
      this.tokenMap.get(config.CLIENT_ID_VALUE) ||
      ({} as AuthAccessTokenDetails)
    );
  }

  isTokenValid(config: OauthAEMConfig, token: AuthAccessTokenDetails): boolean {
    const refreshTokenTime = config?.REFRESH_TOKEN_TIME
      ? config.REFRESH_TOKEN_TIME
      : 300000;
    return (
      this.validateClientId(config, token) &&
      !(this.checkTokenExpiryTime(token) <= refreshTokenTime)
    );
  }

  validateClientId(
    config: OauthAEMConfig,
    token: AuthAccessTokenDetails
  ): boolean {
    const decodedToken: DecodedToken = jwtDecode(token.access_token);
    if (decodedToken && decodedToken.client_id === config.CLIENT_ID_VALUE) {
      return true;
    } else {
      return false;
    }
  }

  checkTokenExpiryTime(token: AuthAccessTokenDetails): number {
    const tokenValidityTime =
      token.token_issued_time +
      (token?.expires_in ? token.expires_in : 1799) * 1000;
    return tokenValidityTime - new Date().getTime();
  }
}
